import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Typography, CardContent, Box } from "@mui/material";
import { RISK_MESSAGE_COLOR_MAP } from "../../constants";
import { getRiskMessageLabel, getRiskNameLabel } from "../../helpers";
import {
  getMultiPolygonRisks,
  getSinglePolygonHeaderPie,
} from "../../../shared/riskDescriptionDict";
import RiskDescriptionContent from "../../shared/riskDescription/RiskDescriptionContent";
import RiskPieChart from "../../shared/riskDescription/RiskPieChart";
import SegmentedRiskGauge from "../../shared/SegmentedRiskGauge/SegmentedRiskGauge";
import PolygonRiskCircles from "./polygonRiskCircles";

const DEFAULT_RISK_COLOR = "#9E9E9E";

const RiskItem = ({
  name,
  risk,
  message,
  description = "",
  descriptionDict,
  riskType,
}) => {
  const { t, i18n } = useTranslation();

  const color =
    (message != null && RISK_MESSAGE_COLOR_MAP[message]) || DEFAULT_RISK_COLOR;
  const normalizedRiskType = (riskType || name || "").toLowerCase();

  // Single-polygon assessments show a compact pie next to the circular
  // progress: fire its class breakdown, flood/earthquake their zone coverage.
  const headerPie = useMemo(
    () =>
      getSinglePolygonHeaderPie({
        riskType: normalizedRiskType,
        descriptionDict,
        locale: i18n.resolvedLanguage || i18n.language,
        translate: t,
      }),
    [
      normalizedRiskType,
      descriptionDict,
      i18n.language,
      i18n.resolvedLanguage,
      t,
    ],
  );

  // Multi-polygon assessments list each polygon's risk as a mini circle.
  const polygonRisks = useMemo(
    () => getMultiPolygonRisks(descriptionDict),
    [descriptionDict],
  );

  return (
    <div>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 0,
        }}
      >
        <Box display="flex" alignItems="center" gap={2} textAlign="center">
          <Box>
            <Typography variant="h6">
              {getRiskNameLabel(name, t) ?? "—"}
            </Typography>

            <Typography
              variant="body1"
              style={{ color }}
              fontWeight={600}
              marginTop={1}
            >
              {getRiskMessageLabel(message, t) ?? "—"}
            </Typography>
          </Box>

          <SegmentedRiskGauge
            risk={risk}
            message={message}
            binCount={descriptionDict?.bin_count}
            size={84}
            thickness={8}
            labelVariant="body1"
          />

          {headerPie ? (
            <Box sx={{ width: 100, flexShrink: 0 }}>
              <RiskPieChart
                slices={headerPie.slices}
                colorMode={headerPie.colorMode}
                riskType={normalizedRiskType}
                height={100}
                showLegend={false}
              />
            </Box>
          ) : null}
        </Box>

        {polygonRisks.length ? (
          <Box sx={{ marginTop: "12px", width: "100%" }}>
            <PolygonRiskCircles
              polygons={polygonRisks}
              binCount={descriptionDict?.bin_count}
            />
          </Box>
        ) : null}

        <Box sx={{ marginTop: "15px", width: "100%" }}>
          <RiskDescriptionContent
            riskType={normalizedRiskType}
            description={description}
            descriptionDict={descriptionDict}
            risk={risk}
            dense
          />
        </Box>
      </CardContent>
    </div>
  );
};

export default RiskItem;
